import * as React from 'react';
import { useLocation } from 'react-router-dom';
import {
  CPG_HEADER_LOGO_URL,
  CPG_MEDIA_KIT_URL,
  CPG_NEWSLETTER_URL,
  CPG_PODCASTS_URL,
  CPG_SLACK_URL,
  INSIDER_CTA_DEFAULT,
  getCpgNavLinks,
  getInsiderCtaCampaign,
  getInsiderCtaUrl,
} from './cpgSites';
import { CpgAuthSlot } from './CpgAuthSlot';
import { CpgSearchPanel, CpgSearchToggle, useCpgSearch } from './CpgSearch';
import { HeadphonesIcon, MailIcon, SlackIcon } from './icons';
import './cpg-banners.css';

// Cross-site CPG brand bar at the very top of every Jobs page. Ported from
// Core/NextJS/apps/cpgjobs/app/components/Cpg/CpgHeader.tsx to CRA:
//   next/image  → <img>
//   usePathname → react-router useLocation().pathname
// The mobile "Brands" / auth dropdown state machine, outside-click close, and the
// currentUrl effect are kept verbatim. Unlike the Next port (anonymous-only), the
// auth section renders a live <CpgAuthSlot/> (login/logout) since the Jobs SPA has
// the auth cookie + user-info query available.

const NAV_LINKS = getCpgNavLinks('cpgjobs');
type MobileDropdown = 'brands' | 'auth' | null;

// All three shortcuts are cross-site for cpgjobs, so they open in a new tab.
const SHORTCUTS = [
  { key: 'newsletter', label: 'Newsletter', url: CPG_NEWSLETTER_URL, icon: <MailIcon /> },
  { key: 'slack', label: 'Join Slack', url: CPG_SLACK_URL, icon: <SlackIcon /> },
  { key: 'podcasts', label: 'Podcasts', url: CPG_PODCASTS_URL, icon: <HeadphonesIcon /> },
];

export function CpgBar() {
  const { pathname } = useLocation();
  const [openDropdown, setOpenDropdown] = React.useState<MobileDropdown>(null);
  const [currentUrl, setCurrentUrl] = React.useState('');
  const [insiderCta, setInsiderCta] = React.useState(INSIDER_CTA_DEFAULT);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setInsiderCta(getInsiderCtaCampaign());
  }, []);

  React.useEffect(() => {
    if (!openDropdown) return undefined;
    const handler = (e: MouseEvent) => {
      const node = wrapperRef.current;
      if (!node) return;
      if (e.target instanceof Node && !node.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openDropdown]);

  React.useEffect(() => {
    setCurrentUrl(window.location.href);
  }, [pathname]);

  const closeDropdown = React.useCallback(() => setOpenDropdown(null), []);
  const search = useCpgSearch(wrapperRef, closeDropdown);
  const showBrands = openDropdown === 'brands';
  const showAuth = openDropdown === 'auth';
  const subscribeUrl = getInsiderCtaUrl(currentUrl, insiderCta.campaign);

  // Chevron path — closed (▼) vs open (▲), ported verbatim.
  const chevronPath = showBrands
    ? 'm9 8.121-3.713 3.713-1.06-1.06L9 6l4.773 4.772-1.06 1.061L9 8.121Z'
    : 'm9 9.879 3.713-3.713 1.06 1.06L9 12 4.227 7.227l1.06-1.061L9 9.879Z';

  return (
    <div className="cpg-bar">
      <div
        ref={wrapperRef}
        role="region"
        aria-label="BevNET CPG Media network navigation"
        className={
          'cpg-header cpg-header--cpgjobs'
          + (showBrands ? ' cpg-header--open-brands' : '')
          + (showAuth ? ' cpg-header--open-auth' : '')
          + (search.open ? ' cpg-header--open-search' : '')
        }
      >
        <button
          className="cpg-header__btn-explore cpg-header__nav-item cpg-header__nav-item--btn"
          type="button"
          aria-expanded={showBrands}
          onClick={() => {
            search.close();
            setOpenDropdown((prev) => (prev === 'brands' ? null : 'brands'));
          }}
        >
          <span>Our Brands</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            aria-hidden="true"
          >
            <path fill="currentColor" d={chevronPath} />
          </svg>
        </button>
        <nav className="cpg-header__nav" aria-label="CPG Media Navigation">
          <ul className="cpg-header__nav-list">
            {NAV_LINKS.map((link) => (
              <li key={`cpg-header-nav-${link.key}`} className="cpg-header__nav-item">
                <a
                  className="cpg-header__nav-item--link"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeDropdown}
                >
                  {link.name}
                </a>
              </li>
            ))}
            {SHORTCUTS.map((shortcut) => (
              <li
                key={`cpg-header-shortcut-${shortcut.key}`}
                className="cpg-header__nav-item cpg-header__nav-item--mobile"
              >
                <a
                  className="cpg-header__nav-item--link cpg-header__nav-item--shortcut"
                  href={shortcut.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeDropdown}
                >
                  {shortcut.icon}
                  <span>{shortcut.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <a
          className="cpg-header__cpg-media-logo"
          href={CPG_MEDIA_KIT_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="BevNET CPG Media"
        >
          <img
            src={CPG_HEADER_LOGO_URL}
            width={188}
            height={16}
            alt="BevNET CPG Media Logo"
          />
        </a>
        <div className="cpg-header__quick-links">
          <CpgSearchToggle search={search} className="cpg-header__quick-link" tooltip="Search" />
          {SHORTCUTS.map((shortcut) => (
            <a
              key={`cpg-header-quick-link-${shortcut.key}`}
              className="cpg-header__quick-link"
              href={shortcut.url}
              target="_blank"
              rel="noopener noreferrer"
              data-tooltip={shortcut.label}
              aria-label={shortcut.label}
            >
              {shortcut.key === 'podcasts' ? <HeadphonesIcon size={18} /> : shortcut.icon}
            </a>
          ))}
        </div>
        <CpgSearchToggle search={search} className="cpg-header__btn-search" />
        <button
          className="cpg-header__btn-auth"
          type="button"
          aria-label="User account menu"
          aria-expanded={showAuth}
          onClick={() => {
            search.close();
            setOpenDropdown((prev) => (prev === 'auth' ? null : 'auth'));
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
            <path
              fill="#fff"
              d="M5 16.25a5 5 0 1 1 10 0H5Zm5-5.625a3.749 3.749 0 0 1-3.75-3.75A3.749 3.749 0 0 1 10 3.125a3.749 3.749 0 0 1 3.75 3.75 3.749 3.749 0 0 1-3.75 3.75Z"
            />
          </svg>
        </button>
        {/*
         * Auth section — CpgAuthSlot owns the whole `cpg-header__auth` section so it
         * can mirror Nombase: Login link + Insider CTA when anonymous, or the avatar
         * + account dropdown when logged in (the Insider CTA is replaced by the
         * account menu). On mobile the ported `cpg-header--open-auth` class reveals
         * this section via the avatar button above.
         */}
        <CpgAuthSlot
          onNavigate={closeDropdown}
          subscribeUrl={subscribeUrl}
          subscribeText={insiderCta.text}
        />
        <CpgSearchPanel search={search} />
      </div>
    </div>
  );
}

export default CpgBar;
